// ビューカードの利用明細CSVをZeniの取引インポート形式に変換する。
//
// VIEW's NET からダウンロードできる「ご利用明細」CSVは Shift-JIS で、明細の前に
// カードのサマリ行（会員番号 / 対象カード / お支払日 / 今回お支払金額）が付く。
// 明細部のヘッダーは次の列を持つ:
//   ご利用年月日, ご利用箇所, ご利用額, 払戻額, ご請求額（うち手数料・利息）,
//   支払区分（回数）, 今回回数, 今回ご請求額・弁済金（うち手数料・利息）,
//   現地通貨額, 通貨略称, 換算レート
// ヘッダーの直後には「****-****-****-0000 山田　太郎」というカード名義行が挟まり、
// 家族カード分が続く明細では名義行が繰り返される。これらは日付として解釈できないため無視される。
// 楽天カード・PayPayカードと同じくCSVに口座・カテゴリ情報が無いので、
// 口座は呼び出し側で指定し、カテゴリはご利用箇所のキーワードから推定する。

import {
  MERCHANT_CATEGORY,
  matchKeywordCategory,
  normalizeDate,
  parseAmount,
  resolveFallbackCategories,
  splitCsvLine,
  splitCsvLines,
  type CsvCategoryRef,
  type CsvImportRow,
  type CsvParseResult,
} from './csv-common'

export type ViewCardCategoryRef = CsvCategoryRef
export type ViewCardImportRow = CsvImportRow
export type ViewCardParseResult = CsvParseResult

// 店名キーワード表より先に判定するビューカード固有の摘要。
// ビューカードの明細はJR東日本系（Suica・駅ビル・ECサイト）が中心で、
// 汎用の店名表だけでは「ＪＲＥ ＭＡＬＬ」が `ＪＲ` に食われて交通費へ誤分類されるなど
// 取り違えが起きるため、ここで先に振り分ける（配列の順序がそのまま優先順位）。
const VIEW_EXPENSE_OVERRIDE: ReadonlyArray<readonly [string, string]> = [
  // 駅ナカ・駅ビルの物販。後段の `ＪＲ`/`駅` 系キーワードより先に判定する必要がある
  ['ＮＥＷＤＡＹＳ', 'food'], ['ニューデイズ', 'food'], ['ＫＩＯＳＫ', 'food'],
  ['ＪＲＥ ＭＡＬＬ', 'food'], ['ＪＲＥＭＡＬＬ', 'food'], ['ビックカメラ', 'furniture'],
  ['アトレ', 'food'], ['ルミネ', 'clothing'], ['ＮＯＮＯＷＡ', 'food'],
  // Suica入金。Suica残高への移動なので厳密には口座間振替だが、移動先の口座を
  // CSVから特定できないため、`MERCHANT_CATEGORY` の SUICA→交通費に合わせて交通費として扱う
  ['オートチャージ', 'transport'], ['モバイルＳｕｉｃａ', 'transport'], ['Ｓｕｉｃａ', 'transport'],
  // きっぷ・定期券・座席指定券
  ['えきねっと', 'transport'], ['定期券', 'transport'], ['グリーン券', 'transport'],
  ['新幹線', 'transport'], ['指定券', 'transport'],
  // カード・ETCカードの年会費
  ['年会費', 'other'],
]

interface ColumnMap {
  date: number
  desc: number
  gross: number     // ご利用額
  refund: number    // 払戻額
  billed: number    // ご請求額（うち手数料・利息）
  payType: number   // 支払区分（回数）
  installment: number // 今回回数
  localAmount: number // 現地通貨額
  currencyCode: number // 通貨略称
}

// ヘッダー行から各列の位置を特定する。見つからない列は既定の並び順にフォールバックする。
function locateColumns(header: string[]): ColumnMap {
  const find = (keywords: string[], fallback: number) => {
    const idx = header.findIndex((h) => keywords.some((k) => h.includes(k)))
    return idx >= 0 ? idx : fallback
  }
  return {
    date: find(['ご利用年月日', '利用年月日', '利用日', '日付'], 0),
    desc: find(['ご利用箇所', '利用箇所', '利用先', '店名'], 1),
    gross: find(['利用額'], 2),
    refund: find(['払戻額', '払戻'], 3),
    // 「ご請求額（うち手数料・利息）」が先に来るので、後方の「今回ご請求額・弁済金」ではなく当月計上分を拾う
    billed: find(['ご請求額', '請求額'], 4),
    payType: find(['支払区分'], 5),
    // 「支払区分（回数）」にも `回数` が含まれるため、キーワードは `今回回数` に限定する
    installment: find(['今回回数'], 6),
    localAmount: find(['現地通貨額'], 8),
    currencyCode: find(['通貨略称'], 9),
  }
}

// サマリ行を読み飛ばして明細のヘッダー行を探す。
// サマリの「お支払日」「今回お支払金額」に引っかからないよう、日付・金額のキーワードは明細列のものに限定する。
function findHeaderIndex(lines: string[]): number {
  const limit = Math.min(lines.length, 20)
  for (let i = 0; i < limit; i++) {
    const cells = splitCsvLine(lines[i])
    const hasDate = cells.some((c) => c.includes('ご利用年月日') || c.includes('利用年月日') || c.includes('ご利用日'))
    const hasAmount = cells.some((c) => c.includes('利用額') || c.includes('請求額'))
    if (hasDate && hasAmount) return i
  }
  return 0
}

// 一括払いは既定なのでメモに残さない。分割・リボ・ボーナス払いのみ支払区分を記録する。
const ONE_TIME_PAY_TYPES = new Set(['1回払', '1回払い', '1回', '一括', '一括払い'])

// 支払区分・今回回数・外貨情報をメモへ退避する。記録すべきものが無ければ未設定。
function buildNote(payType: string, installment: string, localAmount: string, currencyCode: string): string | undefined {
  const parts: string[] = []
  // 明細は「１回払」のように全角数字で入るため、NFKC正規化してから判定する
  const normalizedPayType = payType.normalize('NFKC').trim()
  const isOneTime = normalizedPayType === '' || ONE_TIME_PAY_TYPES.has(normalizedPayType)
  if (!isOneTime) {
    const times = installment.normalize('NFKC').trim()
    parts.push(times !== '' ? `${normalizedPayType}（今回${times}回目）` : normalizedPayType)
  }
  // 海外利用は現地通貨額と通貨略称が入る（請求額は円貨なのでメモにのみ残す）
  const local = localAmount.normalize('NFKC').trim()
  const currency = currencyCode.normalize('NFKC').trim()
  if (local !== '' && currency !== '') parts.push(`現地通貨 ${local} ${currency}`)

  return parts.length > 0 ? parts.join(' / ') : undefined
}

export function parseViewCardCsv(
  text: string,
  accountId: string,
  categories: ReadonlyArray<ViewCardCategoryRef>,
): ViewCardParseResult {
  const lines = splitCsvLines(text)
  if (lines.length === 0) return { rows: [], parsed: 0, ignored: 0 }

  const headerIndex = findHeaderIndex(lines)
  const col = locateColumns(splitCsvLine(lines[headerIndex]))

  const { expenseIds, fallbackExpense, fallbackIncome } = resolveFallbackCategories(categories)
  const expenseSet = new Set(expenseIds)

  const rows: ViewCardImportRow[] = []
  let ignored = 0

  for (const line of lines.slice(headerIndex + 1)) {
    const cols = splitCsvLine(line)
    const date = normalizeDate(cols[col.date] ?? '')

    // 実際に引き落とされるのは手数料・利息込みの「ご請求額」。欠けている場合は「ご利用額」で代替し、
    // どちらも空の払戻のみの行は「払戻額」をマイナス計上（＝収入）として扱う。
    const billed = parseAmount(cols[col.billed] ?? '')
    const gross = parseAmount(cols[col.gross] ?? '')
    const refund = parseAmount(cols[col.refund] ?? '')
    const rawAmount =
      Number.isFinite(billed) && billed !== 0 ? billed
      : Number.isFinite(gross) && gross !== 0 ? gross
      : Number.isFinite(refund) && refund !== 0 ? -refund
      : NaN
    if (!date || !Number.isFinite(rawAmount) || rawAmount === 0) {
      ignored++
      continue
    }

    const description = (cols[col.desc] ?? '').trim() || 'ビューカード利用'
    const isIncome = rawAmount < 0 // マイナス計上（払戻・キャンセル）は収入扱い
    const amount = Math.abs(rawAmount)

    rows.push({
      date,
      type: isIncome ? 'income' : 'expense',
      category: isIncome
        ? fallbackIncome
        : matchKeywordCategory(description, VIEW_EXPENSE_OVERRIDE, expenseSet)
          ?? matchKeywordCategory(description, MERCHANT_CATEGORY, expenseSet)
          ?? fallbackExpense,
      description,
      amount,
      accountId,
      currency: 'JPY',
      note: buildNote(
        cols[col.payType] ?? '',
        cols[col.installment] ?? '',
        cols[col.localAmount] ?? '',
        cols[col.currencyCode] ?? '',
      ),
    })
  }

  return { rows, parsed: rows.length, ignored }
}
